import * as React from 'react';
import * as PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { flow, get, isNil, map, negate } from 'lodash';
import * as actionTypes from './actionTypes';
import * as selectors from './selectors';
import ChecksPage from './ChecksPage';
import { ChecksTable } from './components/ChecksTable';
import { NoChecks } from './components/NotChecks';
import { EditCheckPopup } from './components/EditCheckPopup';
import { RemoveCheckPopup } from './components/RemoveCheckPopup';
import PreviewReceipt from './components/PreviewReceipt';
import Modal from '../../components/Modal';
import Loading from '../../components/Loading';

const MODAL_STATE_NONE = 'NONE';
const MODAL_STATE_VIEW = 'VIEW';
const MODAL_STATE_EDIT = 'EDIT';
const MODAL_STATE_REMOVE = 'REMOVE';

const DEFAULT_PAGE_SIZE = 10;

class ChecksContainer extends React.Component {
  static propTypes = {
    checks: PropTypes.arrayOf(
      PropTypes.shape({
        receiptId: PropTypes.number.isRequired,
        originDate: PropTypes.string.isRequired,
        originSum: PropTypes.number.isRequired,
        description: PropTypes.string,
        receiptCategory: PropTypes.number,
      }),
    ),
    filterCategories: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.number.isRequired,
        label: PropTypes.string.isRequired,
      }),
    ),
    filterYears: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.number.isRequired,
        label: PropTypes.string.isRequired,
      }),
    ),
    isLoading: PropTypes.bool,
    recordInProcess: PropTypes.bool,
    paginationOptions: PropTypes.object,
    hasData: PropTypes.bool,
    actions: PropTypes.objectOf(PropTypes.func).isRequired,
  };

  state = {
    activeRecord: null,
    modalMode: MODAL_STATE_NONE,
    categoryFilter: { id: 'receiptCategory', value: -1 },
    yearFilter: { id: 'originDate', value: -1 },
    defaultSorted: [
      {
        id: 'originDate',
        desc: true,
      },
    ],
    filtered: [],
  };

  componentDidMount() {
    this.props.actions.fetchData();
  }

  componentDidUpdate(prevProps, prevState) {
    if (!this.props.recordInProcess && prevState.activeRecord !== null) {
      this.resetModal();
    }
  }

  fetchNextPage = (option) => {
    const categoryId =
      this.state.categoryFilter.value === -1 ? null : this.state.categoryFilter.value;
    const year = this.state.yearFilter.value === -1 ? null : this.state.yearFilter.value;
    const size = get(option, 'size', get(this.props, 'paginationOptions.size'));
    const page = get(option, 'page', null);
    const sort = map(
      this.state.defaultSorted,
      (sortObject) => `${sortObject.id},${sortObject.desc ? 'DESC' : 'ASC'}`,
    );
    this.props.actions.fetchNextChecks({ categoryId, year, size, page, sort });
  };

  handleEditRecord = (record) => {
    this.setState({
      activeRecord: record,
      modalMode: MODAL_STATE_EDIT,
    });
  };

  handleEditOkRecord = (values) => {
    const { activeRecord: record } = this.state;
    this.props.actions.requestUpdateRecord(record.receiptId, {
      description: values.description,
      receiptCategory: {
        id: values.receiptCategory.value,
        title: values.receiptCategory.label,
      },
    });
  };

  handleEditCancelRecord = () => {
    this.resetModal();
  };

  handleRemoveRecord = (record) => {
    this.setState({
      activeRecord: record,
      modalMode: MODAL_STATE_REMOVE,
    });
  };

  handleRemoveOkRecord = () => {
    const { activeRecord: record } = this.state;

    this.props.actions.requestRemoveRecord(record.receiptId, {
      receiptId: record.receiptId,
      originDate: record.originDate,
      originSum: record.originSum,
      description: record.description,
      checkCategoryId: record.receiptCategory,
    });
  };

  handleRemoveCancelRecord = () => {
    this.resetModal();
  };

  handleViewRecord = (record) => {
    this.setState({
      activeRecord: record,
      modalMode: MODAL_STATE_VIEW,
    });
  };

  handleCategoryChange = ({ value }) => {
    const categoryFilter = { id: 'receiptCategory.label', value };

    this.setState(
      {
        categoryFilter,
        filtered: [categoryFilter, this.state.yearFilter],
      },
      this.fetchNextPage,
    );
  };

  handleYearChange = ({ value }) => {
    const yearFilter = { id: 'originDate', value };

    this.setState(
      {
        yearFilter,
        filtered: [yearFilter, this.state.categoryFilter],
      },
      this.fetchNextPage,
    );
  };

  handleSortedChange = (newSorted) => {
    this.setState({ defaultSorted: newSorted }, this.fetchNextPage);
  };

  resetModal() {
    this.setState({
      activeRecord: null,
      modalMode: MODAL_STATE_NONE,
    });
  }

  render() {
    const {
      checks,
      filterCategories,
      filterYears,
      isLoading,
      recordInProcess,
      paginationOptions,
      hasData,
    } = this.props;
    const { activeRecord, modalMode } = this.state;

    if (isLoading && !hasData) {
      return <Loading />;
    }

    return (
      <ChecksPage title={'Мои чеки'}>
        {hasData ? (
          <ChecksTable
            data={checks}
            filterCategories={filterCategories}
            filterYears={filterYears}
            categoryFilter={this.state.categoryFilter}
            yearFilter={this.state.yearFilter}
            defaultSorted={this.state.defaultSorted}
            paginationOptions={paginationOptions}
            onView={this.handleViewRecord}
            onUpdate={this.handleEditRecord}
            onRemove={this.handleRemoveRecord}
            fetchNextPage={this.fetchNextPage}
            onYearChange={this.handleYearChange}
            onCategoryChange={this.handleCategoryChange}
            onSortedChange={this.handleSortedChange}
            fetchNextChecks={this.props.actions.fetchNextChecks}
            fetchData={this.props.actions.fetchData}
            isLoading={isLoading}
          />
        ) : (
          <NoChecks />
        )}

        {modalMode === MODAL_STATE_EDIT && (
          <EditCheckPopup
            record={activeRecord}
            inProcess={recordInProcess}
            onOk={this.handleEditOkRecord}
            onCancel={this.handleEditCancelRecord}
          />
        )}

        {modalMode === MODAL_STATE_REMOVE && (
          <RemoveCheckPopup
            record={activeRecord}
            inProcess={recordInProcess}
            onOk={this.handleRemoveOkRecord}
            onCancel={this.handleRemoveCancelRecord}
          />
        )}

        {modalMode === MODAL_STATE_VIEW && (
          <Modal
            showModal={true}
            hasCloseIcon={true}
            type={'large'}
            onClose={this.handleRemoveCancelRecord}
          >
            <PreviewReceipt record={activeRecord} />
          </Modal>
        )}
      </ChecksPage>
    );
  }
}

const mapStateToProps = createStructuredSelector({
  checks: selectors.getReceipts,
  paginationOptions: flow(selectors.getReceiptsResponse, selectors.getPaginationOptions),
  hasData: selectors.hasChecksData,
  filterCategories: selectors.getFilterCategories,
  filterYears: selectors.getFilterYears,
  isLoading: negate(selectors.isLoaded),
  recordInProcess: flow(selectors.getRecord, negate(isNil)),
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      fetchData: () => ({
        type: actionTypes.FETCH_DATA,
        pageSize: DEFAULT_PAGE_SIZE,
        sort: `originDate,Desc`,
      }),
      fetchNextChecks: (options) => ({ type: actionTypes.FETCH_NEXT_CHECKS, options }),
      requestUpdateRecord: (id, record) => ({
        type: actionTypes.REQUEST_UPDATE_RECORD,
        id,
        record,
      }),
      requestRemoveRecord: (id, record) => ({
        type: actionTypes.REQUEST_DELETE_RECORD,
        id,
        record,
      }),
    },
    dispatch,
  ),
  dispatch,
});

export default connect(mapStateToProps, mapDispatchToProps)(ChecksContainer);
